/**
 * Rings this browser when the AI receptionist hands a caller to staff
 * (spec §6 "Browser").
 *
 * Who it is for: staff whose role has `answer_calls`, at a club that has call
 * routing. For everyone else, and whenever the platform answers 503
 * CALLS_NOT_CONFIGURED, it renders its children and nothing more.
 *
 * How it works:
 *  - One tab per browser holds the Twilio Device (`navigator.locks`); other
 *    tabs only show and change the person's status.
 *  - "Turn on call alerts" asks for the microphone and desktop notifications
 *    once, then registers. After a reload it re-registers by itself, but a
 *    browser will not play a ringtone until the page has been clicked, so a
 *    slim banner asks for that click.
 *  - Every 30 s (from a Web Worker timer, which a hidden tab does not
 *    throttle) the leader tells the backend it is here and whether it is
 *    registered. The routing engine only rings a browser seen in the last
 *    150 s. Closing the tab says so straight away.
 *  - The incoming call carries the caller and reason as custom parameters,
 *    so the card shows them at once; the full brief follows from the backend.
 *  - When another person answers first, or the caller gives up, Twilio sends
 *    `cancel` and the card goes away with a toast saying why.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { BellRing, X } from "lucide-react";
import { Button } from "../ui";
import {
  getCallBrief,
  getMyAvailability,
  getVoiceToken,
  sendPresence,
  sendPresenceOffline,
  updateMyAvailability,
  type AvailabilityStatus,
  type CallBrief,
} from "../../api/callsAPI";
import { useHasPermission, usePermissions } from "../../hooks/useHasPermission";
import { safeGetItem, safeSetItem } from "@mediatechgroup/app-version";
import { CallCenterContext, INERT_CALL_CENTER, type CallCenterValue } from "./CallCenterContext";
import IncomingCallCard from "./IncomingCallCard";
import ActiveCallBar from "./ActiveCallBar";
import { describeCallsError } from "./callFormat";
import {
  acquireLeaderLock,
  createTwilioDevice,
  startWorkerInterval,
  type VoiceCall,
  type VoiceDevice,
  type VoiceDeviceFactory,
} from "./voiceRuntime";

export const HEARTBEAT_MS = 30_000;
export const ALERTS_KEY = "callAlertsEnabled";
export const ALERTS_BANNER_DISMISSED_KEY = "callAlertsBannerDismissed";

type Phase = "off" | "checking" | "inert" | "ready";

interface IncomingState {
  call: VoiceCall;
  sessionId: string | null;
  callerLabel: string | null;
  reason: string | null;
  brief: CallBrief | null;
}

interface ActiveState {
  call: VoiceCall;
  sessionId: string | null;
  callerLabel: string;
  familyPath: string | null;
  startedAt: number;
  muted: boolean;
}

export interface CallCenterProviderProps {
  /** The signed-in person is staff on a staff page (the caller decides). */
  enabled: boolean;
  children: React.ReactNode;
  /** Tests and storybook only; production uses the Twilio SDK. */
  deviceFactory?: VoiceDeviceFactory;
}

function userHasInteracted(): boolean {
  const activation = (navigator as any)?.userActivation;
  return !!activation?.hasBeenActive;
}

function showDesktopNotification(title: string, body: string): Notification | null {
  try {
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return null;
    if (typeof document !== "undefined" && document.hasFocus && document.hasFocus()) return null;
    const n = new Notification(title, { body, tag: "cc-incoming-call", requireInteraction: true });
    n.onclick = () => {
      window.focus();
      n.close();
    };
    return n;
  } catch {
    return null;
  }
}

const CallCenterProvider: React.FC<CallCenterProviderProps> = ({
  enabled,
  children,
  deviceFactory = createTwilioDevice,
}) => {
  const navigate = useNavigate();
  const canAnswer = useHasPermission("answer_calls");
  const { loading: permissionsLoading } = usePermissions();

  const [phase, setPhase] = useState<Phase>("off");
  const [status, setStatusState] = useState<AvailabilityStatus>("off");
  const [statusSaving, setStatusSaving] = useState(false);
  const [isLeader, setIsLeader] = useState(false);
  const [alertsEnabled, setAlertsEnabled] = useState(() => safeGetItem(ALERTS_KEY) === "1");
  const [bannerDismissed, setBannerDismissed] = useState(
    () => safeGetItem(ALERTS_BANNER_DISMISSED_KEY) === "1"
  );
  const [needsGesture, setNeedsGesture] = useState(() => !userHasInteracted());
  const [registered, setRegistered] = useState(false);
  const [deviceError, setDeviceError] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<IncomingState | null>(null);
  const [active, setActive] = useState<ActiveState | null>(null);

  const registeredRef = useRef(false);
  const incomingRef = useRef<IncomingState | null>(null);
  const activeRef = useRef<ActiveState | null>(null);
  const notificationRef = useRef<Notification | null>(null);
  incomingRef.current = incoming;
  activeRef.current = active;

  const wanted = enabled && canAnswer && !permissionsLoading;

  // ---------------------------------------------------------------------
  // 1. Is there anything to do here? (own status; routing on; configured)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (!wanted) {
      setPhase("off");
      return undefined;
    }
    let cancelled = false;
    setPhase("checking");
    (async () => {
      const mine = await getMyAvailability();
      if (cancelled) return;
      if (!mine.success) {
        // The tables or the platform are not set up for calls: stay out of the way.
        setPhase("inert");
        return;
      }
      if (mine.data.routing_enabled !== true) {
        // The club's routing switch is off (or this backend predates the
        // field): nobody is rung, so nobody is asked to turn alerts on.
        // It comes from /availability/me because answer_calls alone cannot
        // read /routing-settings.
        setPhase("inert");
        return;
      }
      setStatusState(mine.data.status);
      setPhase("ready");
    })();
    return () => {
      cancelled = true;
    };
  }, [wanted]);

  // ---------------------------------------------------------------------
  // 2. One leader tab per browser
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (phase !== "ready") return undefined;
    const release = acquireLeaderLock(() => setIsLeader(true));
    return () => {
      release();
      setIsLeader(false);
    };
  }, [phase]);

  // ---------------------------------------------------------------------
  // 3. Presence heartbeat (leader with alerts on)
  // ---------------------------------------------------------------------
  const beat = useCallback((isRegistered: boolean) => {
    sendPresence(isRegistered).then((result) => {
      if (!result.success && result.code === "CALLS_NOT_CONFIGURED") setPhase("inert");
    });
  }, []);

  useEffect(() => {
    if (phase !== "ready" || !isLeader || !alertsEnabled) return undefined;
    const stop = startWorkerInterval(() => beat(registeredRef.current), HEARTBEAT_MS);
    const onPageHide = () => {
      sendPresenceOffline();
    };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      stop();
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [phase, isLeader, alertsEnabled, beat]);

  // ---------------------------------------------------------------------
  // 4. Incoming calls
  // ---------------------------------------------------------------------
  const clearNotification = () => {
    notificationRef.current?.close();
    notificationRef.current = null;
  };

  const handleIncoming = useCallback((call: VoiceCall) => {
    const params = call.customParameters || new Map<string, string>();
    const sessionId = params.get("ccSession") || null;
    const callerLabel = params.get("ccCaller") || null;
    const reason = params.get("ccReason") || null;
    setIncoming({ call, sessionId, callerLabel, reason, brief: null });

    call.on("cancel", () => {
      if (incomingRef.current?.call === call) {
        setIncoming(null);
        clearNotification();
        toast.info("Call ended: a teammate answered it, or the caller hung up.");
      }
    });
    call.on("reject", () => {
      if (incomingRef.current?.call === call) setIncoming(null);
      clearNotification();
    });
    call.on("disconnect", () => {
      if (incomingRef.current?.call === call) setIncoming(null);
      if (activeRef.current?.call === call) setActive(null);
      clearNotification();
    });
    call.on("error", (err: { message?: string }) => {
      toast.error(`Call problem: ${err?.message || "the call could not continue"}`);
    });

    notificationRef.current = showDesktopNotification(
      "Incoming call",
      [callerLabel || "A caller", reason ? `about ${reason.replace(/_/g, " ")}` : ""].filter(Boolean).join(" ")
    );

    if (sessionId) {
      getCallBrief(sessionId).then((result) => {
        if (!result.success) return; // the card still has the caller and reason
        setIncoming((prev) => (prev && prev.call === call ? { ...prev, brief: result.data } : prev));
        setActive((prev) =>
          prev && prev.call === call
            ? {
                ...prev,
                callerLabel: result.data.caller.label || prev.callerLabel,
                familyPath: result.data.caller.family_path,
              }
            : prev
        );
      });
    }
  }, []);

  // ---------------------------------------------------------------------
  // 5. The device (leader with alerts on)
  // ---------------------------------------------------------------------
  useEffect(() => {
    if (phase !== "ready" || !isLeader || !alertsEnabled) return undefined;
    let cancelled = false;
    let device: VoiceDevice | null = null;

    const setReg = (value: boolean) => {
      registeredRef.current = value;
      setRegistered(value);
    };

    (async () => {
      const first = await getVoiceToken();
      if (cancelled) return;
      if (!first.success) {
        if (first.code === "CALLS_NOT_CONFIGURED") setPhase("inert");
        else setDeviceError(describeCallsError(first));
        return;
      }
      try {
        device = await deviceFactory(first.data.token);
      } catch (err) {
        setDeviceError("Call alerts could not start in this browser.");
        return;
      }
      if (cancelled) {
        device.destroy();
        return;
      }
      const d = device;
      d.on("registered", () => {
        setReg(true);
        setDeviceError(null);
        beat(true);
      });
      d.on("unregistered", () => {
        setReg(false);
        beat(false);
      });
      d.on("error", (err: { message?: string }) => {
        setDeviceError(err?.message || "Call alerts hit a problem.");
      });
      d.on("tokenWillExpire", async () => {
        const next = await getVoiceToken();
        if (next.success) d.updateToken(next.data.token);
        else setDeviceError(describeCallsError(next));
      });
      d.on("incoming", (call: VoiceCall) => handleIncoming(call));
      try {
        await d.register();
      } catch (err) {
        if (!cancelled) setDeviceError("This browser could not be registered for calls.");
      }
    })();

    return () => {
      cancelled = true;
      if (device) {
        try {
          device.destroy();
        } catch {
          // already gone
        }
      }
      setReg(false);
    };
  }, [phase, isLeader, alertsEnabled, deviceFactory, beat, handleIncoming]);

  // A ringtone cannot play until the page has been clicked once.
  useEffect(() => {
    if (!needsGesture) return undefined;
    const done = () => setNeedsGesture(false);
    document.addEventListener("pointerdown", done, { once: true });
    document.addEventListener("keydown", done, { once: true });
    return () => {
      document.removeEventListener("pointerdown", done);
      document.removeEventListener("keydown", done);
    };
  }, [needsGesture]);

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------
  const enableAlerts = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch {
      setDeviceError(
        "The microphone is blocked. Allow it for this site in your browser settings, then try again."
      );
      return;
    }
    try {
      if (typeof Notification !== "undefined" && Notification.permission === "default") {
        await Notification.requestPermission();
      }
    } catch {
      // Notifications are a nicety; ringing works without them.
    }
    safeSetItem(ALERTS_KEY, "1");
    setDeviceError(null);
    setNeedsGesture(false);
    setAlertsEnabled(true);
  }, []);

  const dismissBanner = () => {
    safeSetItem(ALERTS_BANNER_DISMISSED_KEY, "1");
    setBannerDismissed(true);
  };

  const setStatus = useCallback(async (next: AvailabilityStatus) => {
    setStatusSaving(true);
    const result = await updateMyAvailability({ status: next });
    setStatusSaving(false);
    if (result.success) setStatusState(result.data.status);
    else toast.error(describeCallsError(result));
  }, []);

  const openFamily = (path: string) => navigate(path);

  const accept = () => {
    const current = incomingRef.current;
    if (!current) return;
    clearNotification();
    current.call.accept();
    setActive({
      call: current.call,
      sessionId: current.sessionId,
      callerLabel: current.brief?.caller.label || current.callerLabel || "Unknown caller",
      familyPath: current.brief?.caller.family_path || null,
      startedAt: Date.now(),
      muted: false,
    });
    setIncoming(null);
  };

  const decline = () => {
    const current = incomingRef.current;
    if (!current) return;
    clearNotification();
    current.call.reject();
    setIncoming(null);
  };

  const toggleMute = () => {
    const current = activeRef.current;
    if (!current) return;
    const next = !current.muted;
    current.call.mute(next);
    setActive({ ...current, muted: next });
  };

  const hangUp = () => {
    const current = activeRef.current;
    if (!current) return;
    current.call.disconnect();
    setActive(null);
  };

  // Signing out (enabled turns false) forgets nothing but stops ringing; the
  // device effect cleans up. Turning alerts off is not offered: status Off
  // already stops calls, and the browser setting is per device.
  useEffect(() => {
    if (!wanted) {
      setIncoming(null);
      setActive(null);
    }
  }, [wanted]);

  const value: CallCenterValue = useMemo(
    () =>
      phase === "ready"
        ? {
            active: true,
            isLeader,
            registered,
            alertsEnabled,
            deviceError,
            status,
            statusSaving,
            setStatus,
            enableAlerts,
          }
        : INERT_CALL_CENTER,
    [phase, isLeader, registered, alertsEnabled, deviceError, status, statusSaving, setStatus, enableAlerts]
  );

  const showTurnOn = phase === "ready" && isLeader && !alertsEnabled && !bannerDismissed;
  const showGesture = phase === "ready" && isLeader && alertsEnabled && needsGesture;

  return (
    <CallCenterContext.Provider value={value}>
      {children}
      {showTurnOn && (
        <div
          role="region"
          aria-label="Call alerts"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[65] w-[min(32rem,calc(100vw-2rem))] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-4 flex items-start gap-3"
        >
          <BellRing className="w-5 h-5 mt-0.5 text-gray-500 dark:text-gray-400 flex-shrink-0" aria-hidden="true" />
          <div className="flex-1 text-sm text-gray-700 dark:text-gray-200">
            <p className="font-semibold text-gray-900 dark:text-white">Turn on call alerts</p>
            <p>
              Ring this browser when the AI receptionist passes a caller to staff. Your browser
              will ask to use the microphone and show notifications.
            </p>
            {deviceError && <p className="mt-1 text-red-600 dark:text-red-400">{deviceError}</p>}
            <div className="mt-3 flex gap-2">
              <Button variant="primary" size="sm" onClick={enableAlerts}>
                Turn on call alerts
              </Button>
              <Button variant="secondary" size="sm" onClick={dismissBanner}>
                Not now
              </Button>
            </div>
          </div>
          <Button
            variant="neutral"
            size="sm"
            iconOnly
            type="button"
            aria-label="Close call alerts banner"
            onClick={dismissBanner}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
      {showGesture && (
        <div
          role="status"
          className="fixed top-0 inset-x-0 z-[65] bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100 text-sm text-center py-1.5 px-4 cursor-pointer"
          onClick={() => setNeedsGesture(false)}
        >
          Click anywhere to enable ringing for incoming calls.
        </div>
      )}
      {incoming && (
        <IncomingCallCard
          callerLabel={incoming.callerLabel}
          reason={incoming.reason}
          brief={incoming.brief}
          onAccept={accept}
          onDecline={decline}
          onOpenFamily={openFamily}
        />
      )}
      {active && (
        <ActiveCallBar
          callerLabel={active.callerLabel}
          startedAt={active.startedAt}
          muted={active.muted}
          familyPath={active.familyPath}
          onToggleMute={toggleMute}
          onHangUp={hangUp}
          onOpenFamily={openFamily}
        />
      )}
    </CallCenterContext.Provider>
  );
};

export default CallCenterProvider;
